import { useEffect, useMemo, useState } from 'react';
import { useRequireAdminAuth } from '../../hooks/useAdminAuth';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  getActiveAiModel,
  getAvailableAiModels,
  saveAiModel,
  resetAiModel,
  getStartupGeneration,
  setStartupGeneration,
  type ActiveAiModel,
  type AvailableAiModel,
  type ExcludedAiModel,
} from '../../lib/admin-api';
import { Loader2, AlertCircle, CheckCircle2, Cpu } from 'lucide-react';

function fmtPrice(p: AvailableAiModel['pricing']): string {
  if (!p) return '—';
  return `$${p.inputPerMTok.toFixed(2)} in / $${p.outputPerMTok.toFixed(2)} out per 1M tokens`;
}

function fmtContext(tokens: number): string {
  return `${Math.round(tokens / 1000)}K context`;
}

/** Display label for a model's agentic tool-use transport (see toolRoute on AvailableAiModel). */
function fmtToolRoute(route: AvailableAiModel['toolRoute']): string {
  return route === 'mantle' ? 'openai-compat' : 'converse';
}

export default function AIModelView() {
  const { isLoading: authLoading, isAuthenticated } = useRequireAdminAuth();

  const [active, setActive] = useState<ActiveAiModel | null>(null);
  const [models, setModels] = useState<AvailableAiModel[]>([]);
  const [excludedModels, setExcludedModels] = useState<ExcludedAiModel[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedLatencyMs, setSavedLatencyMs] = useState<number | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  const [startupEnabled, setStartupEnabled] = useState<boolean | null>(null);
  const [startupLoading, setStartupLoading] = useState(false);
  const [startupError, setStartupError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    setStartupLoading(true);
    setStartupError(null);
    getStartupGeneration()
      .then((res) => setStartupEnabled(res.enabled))
      .catch((err) => setStartupError(err instanceof Error ? err.message : 'Failed to load startup generation'))
      .finally(() => setStartupLoading(false));
  }, [isAuthenticated]);

  const handleStartupToggle = async (checked: boolean) => {
    setStartupLoading(true);
    setStartupError(null);
    try {
      await setStartupGeneration(checked);
      setStartupEnabled(checked);
    } catch (err) {
      setStartupError(err instanceof Error ? err.message : 'Failed to update startup generation');
    } finally {
      setStartupLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    setIsLoading(true);
    setLoadError(null);
    Promise.all([getActiveAiModel(), getAvailableAiModels()])
      .then(([act, avail]) => {
        setActive(act);
        setModels(avail.models);
        setExcludedModels(avail.excluded);
      })
      .catch((err) => setLoadError(err instanceof Error ? err.message : 'Failed to load models'))
      .finally(() => setIsLoading(false));
  }, [isAuthenticated]);

  // BAA-excluded models must not appear in the picker at all — not even
  // greyed out. Other exclusion categories (capability gaps, outages,
  // ambiguous retention mappings) stay visible, greyed, with their reason.
  const visibleExcludedModels = useMemo(
    () => excludedModels.filter((m) => m.category !== 'baa'),
    [excludedModels],
  );

  const grouped = useMemo(() => {
    const byProvider = new Map<string, { models: AvailableAiModel[]; excluded: ExcludedAiModel[] }>();
    for (const m of models) {
      const group = byProvider.get(m.provider) ?? { models: [], excluded: [] };
      group.models.push(m);
      byProvider.set(m.provider, group);
    }
    for (const m of visibleExcludedModels) {
      const group = byProvider.get(m.provider) ?? { models: [], excluded: [] };
      group.excluded.push(m);
      byProvider.set(m.provider, group);
    }
    return Array.from(byProvider.entries());
  }, [models, visibleExcludedModels]);

  const selected = models.find((m) => m.id === selectedId) ?? null;

  const handleSave = async () => {
    if (!selected) return;
    setIsSaving(true);
    setSaveError(null);
    setSavedLatencyMs(null);
    try {
      const result = await saveAiModel({ modelId: selected.id });
      setSavedLatencyMs(result.latencyMs);
      const act = await getActiveAiModel();
      setActive(act);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save model');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    setIsResetting(true);
    setSaveError(null);
    setSavedLatencyMs(null);
    try {
      await resetAiModel();
      const act = await getActiveAiModel();
      setActive(act);
      setSelectedId(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to reset model');
    } finally {
      setIsResetting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <AdminLayout title="AI Model">
      {/* Active model card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Cpu className="h-5 w-5" /> Active Model
          </CardTitle>
        </CardHeader>
        <CardContent>
          {active ? (
            <div className="space-y-1">
              <p className="font-mono text-sm">{active.modelId}</p>
              <p className="text-xs text-gray-500">
                {active.source === 'admin-selected'
                  ? `Admin-selected (${active.displayName ?? active.modelId})`
                  : active.defaultSource === 'env'
                    ? 'Env default (AWS_BEDROCK_MODEL_ID)'
                    : 'Built-in default'}
                {active.selectedAt && ` · changed ${new Date(active.selectedAt).toLocaleString()}`}
                {active.verifiedMs !== null && ` · verified in ${active.verifiedMs} ms`}
              </p>
              <p className="text-xs text-gray-500">
                Changes take effect on the next AI request — no deploy needed.
              </p>
              {active.source === 'admin-selected' && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={handleReset}
                  disabled={isResetting}
                >
                  {isResetting && <Loader2 className="h-3 w-3 animate-spin mr-2" />}
                  Reset to default ({active.defaultModelId})
                </Button>
              )}
            </div>
          ) : (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          )}
        </CardContent>
      </Card>

      {active && !active.bedrockProvider && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            The active AI provider is not AWS Bedrock — model selection here has no effect on chat.
          </AlertDescription>
        </Alert>
      )}

      {loadError && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
      )}

      {savedLatencyMs !== null && (
        <Alert className="mb-6 border-green-200 bg-green-50 text-green-800">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription>
            Verified and active — responded in {savedLatencyMs} ms. All new AI requests now use this model.
          </AlertDescription>
        </Alert>
      )}

      {saveError && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{saveError}</AlertDescription>
        </Alert>
      )}

      {/* Startup generation toggle */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <Switch
              id="startup-generation"
              checked={startupEnabled === true}
              disabled={startupLoading || startupEnabled === null}
              onCheckedChange={handleStartupToggle}
              aria-label="Startup generation (single JSON call at login)"
            />
            <Label htmlFor="startup-generation" className="cursor-pointer">
              Startup generation (single JSON call at login)
            </Label>
            {startupLoading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
          </div>
          <p className="text-xs text-gray-500 mt-2 ml-12">
            One model call at login produces the Patient Summary card, what's-new topics, and lab statuses.
            When off, the dashboard falls back to the separate current-status call and the static suggested questions.
            Takes effect on the next login — no deploy needed.
          </p>
          {startupError && (
            <p className="text-xs text-red-600 mt-1 ml-12">{startupError}</p>
          )}
        </CardContent>
      </Card>

      {/* Model picker */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-lg">Available Models</CardTitle>
          <Button onClick={handleSave} disabled={!selected || isSaving || loadError !== null || active?.bedrockProvider === false}>
            {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {isSaving ? 'Testing model…' : 'Save & Verify'}
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
          ) : models.length === 0 && visibleExcludedModels.length === 0 ? (
            <p className="text-gray-500 text-sm">No models available.</p>
          ) : (
            <div className="space-y-6">
              {grouped.map(([provider, group]) => (
                <div key={provider}>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">{provider}</h3>
                  <div className="space-y-1">
                    {group.models.map((m) => {
                      const isSelected = selectedId === m.id;
                      const isActive = active?.modelId === m.id;
                      return (
                        <button
                          key={m.id}
                          type="button"
                          data-testid={`model-option-${m.id}`}
                          onClick={() => { setSelectedId(m.id); setSavedLatencyMs(null); setSaveError(null); }}
                          className={`w-full text-left rounded-md border px-3 py-2 transition-colors ${
                            isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <span className="flex items-center gap-2">
                              <span className="text-sm font-medium">{m.displayName}</span>
                              {m.reasoning && (
                                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-purple-50 text-purple-700">
                                  reasoning
                                </span>
                              )}
                              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-50 text-gray-500">
                                tools: {fmtToolRoute(m.toolRoute)}
                              </span>
                              {isActive && (
                                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-green-50 text-green-700">
                                  active
                                </span>
                              )}
                            </span>
                            <span className="text-xs text-gray-500">
                              {fmtContext(m.contextWindowTokens)} · {fmtPrice(m.pricing)}
                            </span>
                          </div>
                          <p className="font-mono text-xs text-gray-400 mt-0.5">{m.id}</p>
                        </button>
                      );
                    })}
                    {group.excluded.map((m) => (
                      <div
                        key={m.id}
                        data-testid={`model-excluded-${m.id}`}
                        className="w-full text-left rounded-md border border-gray-100 px-3 py-2 opacity-50"
                      >
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="text-sm font-medium">{m.displayName}</span>
                          <span className="text-xs text-red-400">{m.reason}</span>
                        </div>
                        <p className="font-mono text-xs text-gray-400 mt-0.5">{m.id}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <p className="text-xs text-gray-400">
                Prices are AWS Bedrock on-demand rates from the AWS Price List API.{' '}
                Models without a matched price show “—” — see the{' '}
                <a
                  href="https://aws.amazon.com/bedrock/pricing/"
                  target="_blank"
                  rel="noreferrer"
                  className="underline"
                >
                  Bedrock pricing page
                </a>.
              </p>
              {visibleExcludedModels.length > 0 && (
                <p className="text-xs text-gray-400">
                  BAA-ineligible models are not shown. Models shown greyed out are unavailable for the reason listed
                  (e.g. no tool-calling support, a catalog outage, or an ambiguous retention mapping under review).
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
